import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { collection, doc, onSnapshot, type FirestoreError } from 'firebase/firestore';
import {
  expiredFromTrash,
  isInTrash,
  normalizeSettings,
  normalizeWishlistItem,
  type Movie,
  type ParsedImport,
  type Watch,
  type WishlistItem,
} from '@physical-movies/core';
import { CollectionContext, type CollectionStatus } from './context';
import { COLLECTIONS, SETTINGS_DOC, getDb } from '../firebase';
import * as repository from '../data/repository';

/**
 * Live view of the whole collection.
 *
 * A personal shelf is a few hundred documents, so the app subscribes to all of
 * it once and does its searching, sorting and statistics in memory. That keeps
 * filtering instant and means no composite indexes to deploy.
 */

type Loaded = { movies: Movie[]; watches: Watch[]; wishlist: WishlistItem[] };

export function CollectionProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<Loaded>({ movies: [], watches: [], wishlist: [] });
  const [settings, setSettings] = useState(() => normalizeSettings(null));
  const [loaded, setLoaded] = useState({
    movies: false,
    watches: false,
    wishlist: false,
    settings: false,
  });
  const [failure, setFailure] = useState<'denied' | 'error' | null>(null);

  // Derived, not stored: a second piece of state for "are we ready yet" could
  // only ever disagree with the subscriptions it summarises. Settings is in
  // here so the shelf never paints in one sort order and then jumps to the
  // stored one a moment later.
  const status: CollectionStatus =
    failure ??
    (loaded.movies && loaded.watches && loaded.wishlist && loaded.settings ? 'ready' : 'loading');

  useEffect(() => {
    const db = getDb();

    const onError = (error: FirestoreError) => {
      // The expected outcome for an account that is not on the allowlist.
      setFailure(error.code === 'permission-denied' ? 'denied' : 'error');
      if (error.code !== 'permission-denied') {
        console.error('Firestore subscription failed', error);
      }
    };

    const subscribe = <K extends keyof Loaded>(name: K, path: string) =>
      onSnapshot(
        collection(db, path),
        (snapshot) => {
          setData((current) => ({
            ...current,
            [name]: snapshot.docs.map((entry) => entry.data() as Loaded[K][number]),
          }));
          setLoaded((current) => ({ ...current, [name]: true }));
        },
        onError,
      );

    const unsubscribers = [
      subscribe('movies', COLLECTIONS.movies),
      subscribe('watches', COLLECTIONS.watches),
      subscribe('wishlist', COLLECTIONS.wishlist),
      // One document rather than a collection, and it legitimately does not
      // exist yet on a shelf that has never opened Settings — an absent
      // snapshot is a valid answer, not an error.
      onSnapshot(
        doc(db, COLLECTIONS.settings, SETTINGS_DOC),
        (snapshot) => {
          setSettings(normalizeSettings(snapshot.data() ?? null));
          setLoaded((current) => ({ ...current, settings: true }));
        },
        onError,
      ),
    ];

    return () => {
      for (const unsubscribe of unsubscribers) unsubscribe();
    };
  }, []);

  /**
   * The wishlist arrives with entries written by older versions of the app, so
   * it is normalised once here and every screen downstream can assume the
   * fields exist.
   */
  const wishlist = useMemo(() => {
    const all = data.wishlist.map(normalizeWishlistItem);
    return {
      live: all.filter((item) => !isInTrash(item)),
      trash: all.filter(isInTrash),
      expired: expiredFromTrash(all).map((item) => item.id),
    };
  }, [data.wishlist]);

  // No server to run a scheduled job on, so the purge rides along with a load.
  const expiredKey = wishlist.expired.join(',');
  useEffect(() => {
    if (!expiredKey) return;
    void repository.purgeExpiredWishlistItems(expiredKey.split(','));
  }, [expiredKey]);

  const indexes = useMemo(() => {
    const movieById = new Map(data.movies.map((movie) => [movie.id, movie]));
    const watchesByMovie = new Map<string, Watch[]>();
    for (const watch of data.watches) {
      const list = watchesByMovie.get(watch.movieId);
      if (list) list.push(watch);
      else watchesByMovie.set(watch.movieId, [watch]);
    }
    // Newest first — that is the order the history list reads in.
    for (const list of watchesByMovie.values()) {
      list.sort((a, b) => b.watchedAt.localeCompare(a.watchedAt));
    }
    return { movieById, watchesByMovie };
  }, [data.movies, data.watches]);

  const value = useMemo(
    () => ({
      status,
      movies: data.movies,
      watches: data.watches,
      wishlist: wishlist.live,
      wishlistTrash: wishlist.trash,
      movieById: indexes.movieById,
      watchesByMovie: indexes.watchesByMovie,
      settings,

      // Written whole: the caller hands back the settings it wants, built with
      // the helpers in core, so there is no half-applied state to reason about.
      saveSettings: repository.saveSettings,

      addMovie: repository.createMovie,
      updateMovie: repository.updateMovie,
      deleteMovie: (movie: Movie) => repository.deleteMovie(movie.id),
      setCopies: (movie: Movie, copies: Movie['copies']) => repository.setCopies(movie.id, copies),

      logWatch: repository.logWatch,
      updateWatch: (watch: Watch, patch: Partial<Watch>) =>
        repository.updateWatch(watch, patch, indexes.watchesByMovie.get(watch.movieId) ?? [watch]),
      deleteWatch: (watch: Watch) =>
        repository.deleteWatch(watch, indexes.watchesByMovie.get(watch.movieId) ?? [watch]),

      addWishlistItem: repository.addWishlistItem,
      updateWishlistItem: repository.updateWishlistItem,
      trashWishlistItem: repository.trashWishlistItem,
      restoreWishlistItem: repository.restoreWishlistItem,
      deleteWishlistItem: repository.deleteWishlistItem,
      moveWishlistItemToCollection: repository.moveWishlistItemToCollection,

      importCollection: async (parsed: ParsedImport) => {
        const summary = await repository.importCollection(
          parsed,
          new Set(data.movies.map((movie) => movie.id)),
        );
        // Restore the location names only onto a shelf that has none of its
        // own — an import merges, and silently renaming somebody's shelves
        // would be a strange thing for "import" to do.
        if (parsed.settings && settings.locations.length === 0) {
          await repository.saveSettings({ ...settings, ...parsed.settings });
        }
        return summary;
      },
    }),
    [status, data.movies, data.watches, wishlist, indexes, settings],
  );

  return <CollectionContext value={value}>{children}</CollectionContext>;
}
